import { abbreviateNumber } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';

import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { BidStreamFilters } from '@/services/bidStream.service';

type AudienceRange = 'low' | 'middle' | 'high';

export function getCartographyFilter(zones: Zone[] | null): BidStreamFilters['cartographic'] | undefined {
  const cartographyZones =
    zones?.filter(zone => zone.country && zone.cartographyLevelId && zone.cartographyLevel) || [];
  if (cartographyZones.length === 0) {
    return undefined;
  }
  const cartographyFilter = cartographyZones.reduce(
    (acc, zone) => {
      const level = zone.cartographyLevel as string;
      const zoneId = zone.cartographyLevelId as string;
      if (!acc[level]) {
        acc[level] = [];
      }
      acc[level].push(zoneId);
      return acc;
    },
    {} as { [level: string]: string[] }
  );

  return Object.entries(cartographyFilter).map(([level, ids]) => ({ level, values: ids }));
}

export function getAudienceFilter(targeting: TargetingBaseType): BidStreamFilters['audience'] | undefined {
  const getGender = () => {
    if (targeting.gender === undefined || targeting?.gender?.length === 0) {
      return undefined;
    }
    const gender = targeting.gender[0] === 'f' ? 'female' : 'male';

    return [gender];
  };
  const getAge = () => {
    const athenaAgeGroups = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
    let { min, max } = (targeting.ages?.[0] as Record<string, any>) || {};
    min = Number.parseInt(min);
    max = Number.parseInt(max);
    const ages = athenaAgeGroups.filter(group => {
      const [minGroupRaw, maxGroupRaw] = group.replace('+', '').split('-');
      const minGroup = Number.parseInt(minGroupRaw);
      const maxGroup = Number.parseInt(maxGroupRaw);
      if (min <= minGroup && max >= maxGroup) {
        return true;
      }
      if (min >= minGroup && max <= maxGroup) {
        return true;
      }
      if ((min >= minGroup && min <= maxGroup) || (max >= minGroup && max <= maxGroup)) {
        return true;
      }
      if (!maxGroup && (min >= minGroup || max >= minGroup)) {
        return true;
      }
      return false;
    });

    return ages.length ? ages : undefined;
  };
  const audienceFilter = {
    age: getAge(),
    gender: getGender()
  };

  return Object.values(audienceFilter).some(Boolean) ? audienceFilter : undefined;
}

export function audienceToPercentage(audience: number): number {
  if (audience < 10000) {
    return Math.round((audience / 10000) * 33);
  }
  if (audience <= 1000000) {
    return Math.round(((audience - 10000) / (1000000 - 10000)) * (66 - 34) + 34);
  }
  return Math.min(100, Math.round(((audience - 1000000) / 1000000) * (100 - 67) + 67));
}

export function getAudienceRange(audience: number): AudienceRange {
  if (audience <= 10000) {
    return 'low';
  }
  if (audience <= 1000000) {
    return 'middle';
  }
  return 'high';
}

export function getAudienceRangeValue(audience: number) {
  const getPercentageRange = () => {
    const minRangeValue = audience * 0.8;
    const maxRangeValue = audience * 1.2;
    const roundedMin = minRangeValue > 1000 ? minRangeValue : Math.round(minRangeValue);
    const roundedMax = maxRangeValue > 1000 ? maxRangeValue : Math.round(maxRangeValue);
    return {
      max: abbreviateNumber(roundedMax, Number.isInteger(roundedMax) ? 0 : 1),
      min: abbreviateNumber(roundedMin, Number.isInteger(roundedMin) ? 0 : 1)
    };
  };
  const { min, max } = getPercentageRange();
  return `${min.replace('MM', 'M')} - ${max.replace('MM', 'M')}`;
}
